import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { HomeworkItem, HomeworkSubmission, SubmissionStatus } from './homeworkTypes';
import { fetchStudents, type StudentApiRecord } from '../../services/studentApi';
import { fetchHomeworkSubmissionStatuses, recordHomeworkGrade } from '../../services/homeworkApi';
import './HomeworkEvaluation.css';
import { useTranslation } from 'react-i18next';

interface HomeworkEvaluationProps {
    homework: HomeworkItem | null;
    onBack?: () => void;
}

type StatusDropdownValue = 'pending' | 'submitted' | 'graded' | 'late';
type StatusFilterValue = 'all' | SubmissionStatus;

const statusToDropdownValue: Record<SubmissionStatus, StatusDropdownValue> = {
    pending: 'pending',
    submitted: 'submitted',
    evaluated: 'graded',
    late: 'late',
};

const dropdownValueToStatus: Record<StatusDropdownValue, SubmissionStatus> = {
    pending: 'pending',
    submitted: 'submitted',
    graded: 'evaluated',
    late: 'late',
};

export default function HomeworkEvaluation({ homework, onBack }: HomeworkEvaluationProps) {
    const { t: _t } = useTranslation();
    const [submissions, setSubmissions] = useState<HomeworkSubmission[]>([]);
    const [loading, setLoading] = useState(true);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');

    const marksRefs = useRef<(HTMLInputElement | null)[]>([]);
    const remarksRefs = useRef<(HTMLInputElement | null)[]>([]);

    /* ── Load students for the homework's class/section ── */
    const loadStudents = useCallback((hw: HomeworkItem) => {
        setLoading(true);
        setDirty(false);
        setStatusFilter('all');

        const maxMarks = hw.total_marks != null ? Number(hw.total_marks) : 100;

        const mapStudent = (s: StudentApiRecord, idx: number): HomeworkSubmission => {
            const firstName = s.first_name ?? s.user?.first_name ?? '';
            const lastName = s.last_name ?? s.user?.last_name ?? '';
            const fullName = `${firstName}${lastName ? ' ' + lastName : ''}`.trim();
            const initials = `${firstName.charAt(0)}${lastName ? lastName.charAt(0) : ''}`.toUpperCase();
            return {
                id: s.id,
                student_id: s.id,
                student_name: fullName || 'Unknown',
                roll_number: s.roll_number ? Number(s.roll_number) : idx + 1,
                avatar_initials: initials || '?',
                section_id: s.current_section?.id ?? null,
                status: 'pending',
                submitted_date: null,
                marks: null,
                max_marks: maxMarks,
                remarks: '',
            };
        };

        Promise.all([
            fetchStudents({
                classId: hw.class_id,
                sectionId: hw.section_id || undefined,
                perPage: 200,
            }),
            fetchHomeworkSubmissionStatuses(hw.id).catch(() => []),
        ]).then(([studentsRes, statuses]) => {
            const statusByStudent = new Map<string, SubmissionStatus>();
            for (const row of statuses) {
                statusByStudent.set(row.student_id, row.status);
            }
            setSubmissions(
                studentsRes.data.map((s, idx) => {
                    const sub = mapStudent(s, idx);
                    const apiStatus = statusByStudent.get(s.id);
                    return apiStatus ? { ...sub, status: apiStatus } : sub;
                })
            );
        }).catch(() => {
            setSubmissions([]);
        }).finally(() => {
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (homework) loadStudents(homework);
    }, [homework, loadStudents]);

    /* ── Update marks ── */
    const updateMarks = (id: string, rawValue: string) => {
        const val = rawValue === '' ? null : Number(rawValue);
        setSubmissions((prev) =>
            prev.map((s) => {
                if (s.id !== id) return s;
                const marks = val !== null && val < 0 ? 0 : val;
                return { ...s, marks };
            })
        );
        setDirty(true);
    };

    /* ── Update status ── */
    const updateStatus = (id: string, value: StatusDropdownValue) => {
        const nextStatus = dropdownValueToStatus[value];
        setSubmissions((prev) =>
            prev.map((s) => (s.id !== id ? s : { ...s, status: nextStatus }))
        );
        setDirty(true);
    };

    /* ── Update remarks ── */
    const updateRemarks = (id: string, value: string) => {
        setSubmissions((prev) =>
            prev.map((s) => (s.id !== id ? s : { ...s, remarks: value }))
        );
        setDirty(true);
    };

    /* ── Keyboard navigation (marks column) ── */
    const handleMarksKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number, total: number) => {
        const move = (target: number) => {
            e.preventDefault();
            marksRefs.current[target]?.focus();
            marksRefs.current[target]?.select();
        };
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
            if (idx < total - 1) move(idx + 1);
        } else if (e.key === 'ArrowUp') {
            if (idx > 0) move(idx - 1);
        } else if (e.key === 'Tab' && !e.shiftKey) {
            // Move focus to remarks column
            e.preventDefault();
            remarksRefs.current[idx]?.focus();
        }
    };

    /* ── Keyboard navigation (remarks column) ── */
    const handleRemarksKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number, total: number) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter') {
            e.preventDefault();
            remarksRefs.current[Math.min(idx + 1, total - 1)]?.focus();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            remarksRefs.current[Math.max(idx - 1, 0)]?.focus();
        }
    };

    /* ── Batch save ── */
    const handleSave = async () => {
        if (saving || !homework) return;

        // Only post rows that carry actual evaluation data
        const toRecord = submissions.filter(
            (s) => s.status !== 'pending' || s.marks !== null || s.remarks.trim() !== ''
        );

        if (toRecord.length === 0) {
            setErrorMsg('Nothing to save — enter marks, remarks, or update a status first.');
            setTimeout(() => setErrorMsg(''), 4000);
            return;
        }

        const missingSection = toRecord.filter(
            (s) => !(homework.section_id || s.section_id)
        );
        if (missingSection.length > 0) {
            setErrorMsg(
                `Cannot save: ${missingSection.length} student(s) have no section assigned.`
            );
            setTimeout(() => setErrorMsg(''), 5000);
            return;
        }

        setSaving(true);
        setErrorMsg('');

        const results = await Promise.allSettled(
            toRecord.map((s) =>
                recordHomeworkGrade(homework.id, {
                    student_id: s.student_id,
                    class_id: homework.class_id,
                    section_id: (homework.section_id || s.section_id) as string,
                    marks_obtained: s.marks,
                    teacher_feedback: s.remarks,
                    status: s.status,
                })
            )
        );

        const failures = results.filter((r) => r.status === 'rejected').length;
        const successes = results.length - failures;

        setSaving(false);

        if (failures === 0) {
            setDirty(false);
            setSuccessMsg(
                `Saved ${successes} evaluation${successes === 1 ? '' : 's'} successfully!`
            );
            setTimeout(() => setSuccessMsg(''), 5000);
        } else if (successes === 0) {
            setErrorMsg(`Failed to save evaluations. Please try again.`);
            setTimeout(() => setErrorMsg(''), 6000);
        } else {
            setErrorMsg(
                `Saved ${successes}, but ${failures} failed. Please retry.`
            );
            setTimeout(() => setErrorMsg(''), 6000);
        }
    };

    /* ── Stats ── */
    const totalStudents = submissions.length;
    const submittedCount = submissions.filter(
        (s) => s.status === 'submitted' || s.status === 'evaluated' || s.status === 'late'
    ).length;
    const evaluatedCount = submissions.filter((s) => s.status === 'evaluated' || s.marks !== null).length;
    const pendingCount = submissions.filter((s) => s.status === 'pending').length;

    /* ── Apply status filter ── */
    const filteredSubmissions = useMemo(() => {
        if (statusFilter === 'all') return submissions;
        return submissions.filter((s) => s.status === statusFilter);
    }, [submissions, statusFilter]);

    /* ── Helpers ── */
    const formatDate = (d: string | null) => {
        if (!d) return '—';
        const date = new Date(d + 'T00:00:00');
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
        });
    };

    const showMarks = homework?.total_marks != null;

    if (!homework) {
        return (
            <div className="hw-eval">
                <div className="hw-eval__empty-state">
                    <div className="hw-eval__empty-icon">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                    </div>
                    <h3 className="hw-eval__empty-title">No Homework Selected</h3>
                    <p className="hw-eval__empty-text">Select a homework from the Homework Ledger to begin evaluation.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="hw-eval">
            {successMsg && (
                <div className="hw-eval__toast">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="hw-eval__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {errorMsg && (
                <div className="hw-eval__toast hw-eval__toast--error">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                    <span>{errorMsg}</span>
                    <button className="hw-eval__toast-dismiss" onClick={() => setErrorMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* Header */}
            <div className="hw-eval__header">
                <div className="hw-eval__header-text">
                    <h1 className="hw-eval__title">Evaluate: {homework.title}</h1>
                    <p className="hw-eval__subtitle">Grade student submissions and provide feedback.</p>
                </div>
                <button className="hw-eval__back-btn" onClick={onBack}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                    Back to Homework
                </button>
            </div>

            {/* Context card */}
            <div className="hw-eval__context">
                <div className="hw-eval__context-item">
                    <span className="hw-eval__context-label">Subject</span>
                    <span className="hw-eval__context-value">{homework.subject_name}</span>
                </div>
                <div className="hw-eval__context-item">
                    <span className="hw-eval__context-label">Target</span>
                    <span className="hw-eval__context-value">
                        {homework.section_id === null ? (
                            <span className="hw-eval__context-badge hw-eval__context-badge--broadcast">
                                {homework.class_name} (All Sections)
                            </span>
                        ) : (
                            <span className="hw-eval__context-badge hw-eval__context-badge--section">
                                {homework.class_name} – {homework.section_name}
                            </span>
                        )}
                    </span>
                </div>
                <div className="hw-eval__context-item">
                    <span className="hw-eval__context-label">Due Date</span>
                    <span className="hw-eval__context-value">{formatDate(homework.due_date)}</span>
                </div>
            </div>

            {/* Stats bar */}
            {!loading && (
                <div className="hw-eval__stats-bar">
                    <div className="hw-eval__stat">
                        <span className="hw-eval__stat-value">{totalStudents}</span>
                        <span className="hw-eval__stat-label">Total</span>
                    </div>
                    <div className="hw-eval__stat hw-eval__stat--submitted">
                        <span className="hw-eval__stat-value">{submittedCount}</span>
                        <span className="hw-eval__stat-label">Submitted</span>
                    </div>
                    <div className="hw-eval__stat hw-eval__stat--evaluated">
                        <span className="hw-eval__stat-value">{evaluatedCount}</span>
                        <span className="hw-eval__stat-label">Evaluated</span>
                    </div>
                    <div className="hw-eval__stat hw-eval__stat--pending">
                        <span className="hw-eval__stat-value">{pendingCount}</span>
                        <span className="hw-eval__stat-label">Pending</span>
                    </div>
                    <div className="hw-eval__stat-spacer" />
                    <div className="hw-eval__filter">
                        <label className="hw-eval__filter-label" htmlFor="hw-eval-status-filter">Filter</label>
                        <select
                            id="hw-eval-status-filter"
                            className="hw-eval__filter-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as StatusFilterValue)}
                        >
                            <option value="all">All ({totalStudents})</option>
                            <option value="pending">Pending ({pendingCount})</option>
                            <option value="submitted">
                                Submitted ({submissions.filter((s) => s.status === 'submitted').length})
                            </option>
                            <option value="evaluated">
                                Evaluated ({submissions.filter((s) => s.status === 'evaluated').length})
                            </option>
                            <option value="late">
                                Late ({submissions.filter((s) => s.status === 'late').length})
                            </option>
                        </select>
                    </div>
                </div>
            )}

            {/* Submissions grid */}
            {loading ? (
                <div className="hw-eval__loading">
                    <div className="hw-eval__loader">
                        <span className="hw-eval__loader-dot" />
                        <span className="hw-eval__loader-dot" />
                        <span className="hw-eval__loader-dot" />
                    </div>
                </div>
            ) : (
                <>
                    <div className="hw-eval__grid-wrap">
                        <table className="hw-eval__table">
                            <thead>
                                <tr>
                                    <th className="hw-eval__th hw-eval__th--roll">Roll No</th>
                                    <th className="hw-eval__th">Student Name</th>
                                    <th className="hw-eval__th hw-eval__th--status">Status</th>
                                    <th className="hw-eval__th hw-eval__th--date">Submitted</th>
                                    {showMarks && <th className="hw-eval__th hw-eval__th--marks">Marks</th>}
                                    <th className="hw-eval__th hw-eval__th--remarks">Teacher Remarks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredSubmissions.length === 0 ? (
                                    <tr>
                                        <td className="hw-eval__td hw-eval__empty-row" colSpan={showMarks ? 6 : 5}>
                                            No students match the selected filter.
                                        </td>
                                    </tr>
                                ) : filteredSubmissions.map((s, idx) => {
                                    const isOverMax = s.marks !== null && s.marks > s.max_marks;
                                    const total = filteredSubmissions.length;
                                    return (
                                        <tr className="hw-eval__row" key={s.id}>
                                            <td className="hw-eval__td hw-eval__td--roll">{s.roll_number}</td>
                                            <td className="hw-eval__td">
                                                <div className="hw-eval__student">
                                                    <span className="hw-eval__avatar">{s.avatar_initials}</span>
                                                    <span className="hw-eval__student-name">{s.student_name}</span>
                                                </div>
                                            </td>
                                            <td className="hw-eval__td" style={{ textAlign: 'center' }}>
                                                <select
                                                    className={`hw-eval__status-select hw-eval__status-select--${s.status}`}
                                                    value={statusToDropdownValue[s.status]}
                                                    onChange={(e) => updateStatus(s.id, e.target.value as StatusDropdownValue)}
                                                    aria-label="Update status"
                                                >
                                                    {s.status === 'pending' && <option value="pending">Pending</option>}
                                                    <option value="submitted">Submitted</option>
                                                    {s.marks !== null && <option value="graded">Graded</option>}
                                                    <option value="late">Late</option>
                                                </select>
                                            </td>
                                            <td className="hw-eval__td hw-eval__td--date">{formatDate(s.submitted_date)}</td>
                                            {showMarks && (
                                                <td className="hw-eval__td" style={{ textAlign: 'right' }}>
                                                    <input
                                                        ref={(el) => { marksRefs.current[idx] = el; }}
                                                        className={`hw-eval__marks-input ${isOverMax ? 'hw-eval__marks-input--error' : ''}`}
                                                        type="number"
                                                        min={0}
                                                        max={s.max_marks}
                                                        value={s.marks !== null ? s.marks : ''}
                                                        placeholder="—"
                                                        onChange={(e) => updateMarks(s.id, e.target.value)}
                                                        onKeyDown={(e) => handleMarksKeyDown(e, idx, total)}
                                                    />
                                                    <span className="hw-eval__marks-max">/ {s.max_marks}</span>
                                                </td>
                                            )}
                                            <td className="hw-eval__td">
                                                <input
                                                    ref={(el) => { remarksRefs.current[idx] = el; }}
                                                    className="hw-eval__remarks-input"
                                                    type="text"
                                                    placeholder="Add remarks…"
                                                    value={s.remarks}
                                                    onChange={(e) => updateRemarks(s.id, e.target.value)}
                                                    onKeyDown={(e) => handleRemarksKeyDown(e, idx, total)}
                                                />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Sticky save bar */}
                    <div className="hw-eval__save-bar">
                        <button
                            className="hw-eval__save-btn"
                            onClick={handleSave}
                            disabled={saving || !dirty}
                        >
                            {saving ? (
                                <>
                                    <svg className="hw-eval__spinner" width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" strokeWidth="3" /></svg>
                                    Saving…
                                </>
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
                                    Save Evaluations
                                </>
                            )}
                        </button>
                        <span className="hw-eval__save-hint">
                            {dirty ? 'You have unsaved changes' : 'All evaluations saved'}
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}
